import React, { useState } from "react";
import Reaction from "./Reaction";
import "./ScienceLab.css";

const ingredients = ["🔥", "💧", "🌱", "⚡", "🧊", "☀️", "🌙", "🌪", "🪨", "🍄", "🌈"];

const reactions = {
  "🔥+💧": "💨 Steam!",
  "🔥+🧊": "💧 Water!",
  "🔥+⚡": "💥 Explosion!",
  "🔥+🌱": "🍂 Burnt Leaves!",
  "🔥+🪨": "🌋 Lava!",
  "💧+🌱": "🌸 Flower!",
  "💧+⚡": "🌩 Thunderstorm!",
  "💧+🧊": "🧊 Iceberg!",
  "💧+☀️": "🌊 Evaporation!",
  "💧+🌪": "🌪 Hurricane!",
  "🌱+⚡": "🍎 Fruit!",
  "🌱+🧊": "❄️ Frozen Plant!",
  "🌱+☀️": "🌻 Sunflower!",
  "🌱+🌙": "🌙 Night Bloom!",
  "🌱+🍄": "🍄 Mushroom Patch!",
  "⚡+🧊": "❄️ Snowstorm!",
  "⚡+☀️": "⚡ Solar Flare!",
  "⚡+🌪": "🌪 Superstorm!",
  "🧊+☀️": "💧 Melted Water!",
  "🧊+🌙": "🌌 Frosty Night!",
  "☀️+🌈": "🌈 Rainbow!",
  "☀️+🍄": "🍄 Sun‑dried Mushrooms!",
  "🌙+💧": "🌊 Tides!",
  "🌙+⚡": "🌌 Lunar Storm!",
  "🌙+🌈": "🌈 Moonbow!",
  "🌪+🔥": "🔥 Firestorm!",
  "🌪+🧊": "❄️ Blizzard!",
  "🪨+💧": "🪨 Mud!",
  "🪨+⚡": "⚡ Magnetite!",
  "🍄+⚡": "🍄 Electric Fungus!",
  "🍄+🌙": "🍄 Glowing Mushrooms!",
  "🌈+⚡": "🌈 Lightning Rainbow!"
};

const reactionFacts = {
  "🔥+💧": "Fire heats water and makes steam.",
  "🔥+🧊": "Fire melts ice into water.",
  "🔥+⚡": "Fire and lightning make a big blast.",
  "🔥+🌱": "Fire burns plants into dry leaves.",
  "🔥+🪨": "Fire melts rock into lava.",
  "💧+🌱": "Water helps plants grow flowers.",
  "💧+⚡": "Water and lightning make a storm.",
  "💧+🧊": "Water freezes into icebergs.",
  "💧+☀️": "Sun makes water evaporate.",
  "💧+🌪": "Water and wind form hurricanes.",
  "🌱+⚡": "Lightning gives plants energy to grow fruit.",
  "🌱+🧊": "Cold freezes plants.",
  "🌱+☀️": "Sunlight helps plants bloom.",
  "🌱+🌙": "Moonlight makes plants bloom at night.",
  "🌱+🍄": "Plants and mushrooms grow together.",
  "⚡+🧊": "Lightning in ice makes snowstorms.",
  "⚡+☀️": "Lightning and sun create solar flares.",
  "⚡+🌪": "Lightning and tornado make superstorms.",
  "🧊+☀️": "Sun melts ice into water.",
  "🧊+🌙": "Moonlight makes icy nights.",
  "☀️+🌈": "Sunlight and rain make rainbows.",
  "☀️+🍄": "Sun dries mushrooms.",
  "🌙+💧": "Moon pulls water to make tides.",
  "🌙+⚡": "Moon and lightning make lunar storms.",
  "🌙+🌈": "Moonlight and rain make moonbows.",
  "🌪+🔥": "Wind and fire make firestorms.",
  "🌪+🧊": "Wind and ice make blizzards.",
  "🪨+💧": "Water turns rock into mud.",
  "🪨+⚡": "Lightning makes rocks magnetic.",
  "🍄+⚡": "Lightning makes glowing mushrooms.",
  "🍄+🌙": "Mushrooms glow at night.",
  "🌈+⚡": "Lightning makes rainbow sparks."
};

export default function ScienceLab() {
  const [selected, setSelected] = useState([]);
  const [combo, setCombo] = useState("");
  const [result, setResult] = useState("");
  const [message, setMessage] = useState("");
  const [fact, setFact] = useState("");

  const playSound = (file) => {
    const audio = new Audio(file);
    audio.play();
  };

  const mixIngredients = () => {
    if (selected.length >= 2) {
      const comboStr = selected.join("+");
      setCombo(comboStr);
      if (reactions[comboStr]) {
        setResult(reactions[comboStr]);
        setMessage("✅ Correct Reaction!");
        playSound("/sounds/correct.mp3");
        setTimeout(() => playSound("/sounds/win.mp3"), 500);
      } else {
        setResult("❌ No reaction...");
        setMessage("Try again!");
        playSound("/sounds/wrong.wav");
      }
      setSelected([]);
      setFact("");
    }
  };

  const explainReaction = () => {
    if (combo && reactionFacts[combo]) {
      const explanation = reactionFacts[combo];
      setFact(explanation);

      // Voice narration
      const utterance = new SpeechSynthesisUtterance(explanation);
      utterance.lang = "en-US"; // you can change to "en-IN" for Indian English
      window.speechSynthesis.speak(utterance);
    } else {
      setFact("No simple fact available for this combo.");
    }
  };

  return (
    <div className="app">
      <h1>🔬 Science Lab</h1>
      <p>Mix emoji ingredients to discover reactions!</p>

      <div className="ingredients">
        {ingredients.map((ing, i) => (
          <button
            key={i}
            className={selected.includes(ing) ? "selected" : ""}
            onClick={() =>
              setSelected((prev) =>
                prev.includes(ing) ? prev : [...prev, ing]
              )
            }
          >
            {ing}
          </button>
        ))}
      </div>

      <button className="mix-btn" onClick={mixIngredients}>
        Mix!
      </button>

      {combo && (
        <div className="combo">
          <h2>🧪 Combo: {combo}</h2>
        </div>
      )}

      <Reaction result={result} />
      <p className="message">{message}</p>

      {result && (
        <button className="mix-btn" onClick={explainReaction}>
          Explain Reaction 🔊
        </button>
      )}

      {fact && <p className="fact">ℹ️ {fact}</p>}
    </div>
  );
}
